/*
 * A person's or a group's picture: the uploaded one where there is one, and otherwise the initials on
 * one of the ramp's eight backgrounds, picked by the account id so that the same person keeps the
 * same colour on every screen and a list of five people reads as five people rather than as five
 * identical squares.
 *
 * A url that does not load falls back to the initials rather than leaving a broken picture on screen.
 * It is a normal state and not a failure: an avatar whose attachment its owner has removed, or whose
 * bytes this reader may not see, answers with a refusal, and the account still has to be drawn.
 *
 * The shape is a rounded square, not a circle: a square reads as an identity mark, a circle reads as
 * a status dot. The one real status dot is drawn in its corner when the picture is asked to carry
 * one, over the picture and never beside it — a list must not move when somebody signs in.
 *
 * The image is decorative: every place that shows an avatar shows the name beside it, so an `alt`
 * would make a screen reader read the same person twice. The dot is decorative for the same reason:
 * the two surfaces a person is *looked up* on — the profile and the hover card — say the state in
 * words, and everywhere else it is a hint the eye reads in passing.
 */

import { useState, type CSSProperties } from "react"
import "./people.css"
import { initials } from "./person.ts"
import type { Presence } from "./presence.ts"

export type AvatarSize = "xs" | "sm" | "md" | "lg"

/**
 * How many backgrounds the ramp offers. It is the other half of `--color-avatar-1 … -N` in
 * tokens.css: adding a colour is one token there and this number raised by one — scripts/
 * checkContrast.ts finds the new token by itself and asserts the initials stay legible on it.
 */
const AVATAR_TONES = 8

/**
 * The tone of one account, as the number of its token.
 *
 * Account 1 takes tone 1, account 2 tone 2, and so on round the ramp. The offset is what makes that
 * true: `id % AVATAR_TONES` gave account 1 the *second* tone and pushed tone 1 — the one dark hue
 * with white initials, which is the ramp's whole answer to a reader who sees no colour at all — to
 * account 8, so a team of seven never saw it (X-B2 B2-13). Both formulas first repeat at account 9,
 * so the step costs no collision. `+ AVATAR_TONES` keeps the remainder positive for an id of
 * nought, which no caller sends and which would otherwise ask the stylesheet for a token that does
 * not exist.
 */
function toneOf(id: number): number {
	return 1 + ((id - 1 + AVATAR_TONES) % AVATAR_TONES)
}

/**
 * The colour of one account's face, as a value a style may hold. A surface that marks something as
 * *this person's* without drawing their face — the paragraph somebody else is writing in, on a
 * document page — asks for it here, so one account has one colour wherever it appears.
 */
export function avatarTone(id: number): string {
	return `var(--color-avatar-${String(toneOf(id))})`
}

/**
 * How many letters the fallback of each size carries. The two small squares take one: 20 px is too
 * small for two 13 px letters at all, and the 24 px face is the one the thread footer overlaps by
 * 6 px, where a second letter is half-covered by the next face and reads as another letter entirely
 * ("BB" as "BE"). The faces a person is looked up on — the row's 36 px, the profile's 96 — take the
 * first letters of the first two words.
 */
const INITIALS_BY_SIZE: Record<AvatarSize, number> = { xs: 1, sm: 1, md: 2, lg: 2 }

interface AvatarProps {
	name: string
	url?: string | undefined
	size?: AvatarSize
	/**
	 * The account the picture stands for. It picks the fallback's colour; without it the fallback
	 * stays on the neutral surface, which is what a picture with no account behind it — a group, a
	 * placeholder — should look like.
	 */
	id?: number | undefined
	/** Whether this person is here right now; absent draws no dot and changes no measurement. */
	presence?: Presence | undefined
}

export function Avatar({ name, url, size = "md", id, presence }: AvatarProps) {
	// The url that failed, rather than a flag: a picture that is replaced gets its chance to load,
	// and nothing has to reset anything when the account changes under this component.
	const [brokenUrl, setBrokenUrl] = useState<string | null>(null)
	const className = `tb-avatar tb-avatar--${size}`
	const letters = initials(name, INITIALS_BY_SIZE[size])

	let picture
	if (url !== undefined && url !== "" && url !== brokenUrl) {
		picture = <img className={className} src={url} alt="" onError={() => setBrokenUrl(url)} />
	} else if (id === undefined) {
		picture = (
			<span aria-hidden="true" className={className}>
				{letters}
			</span>
		)
	} else {
		// The tone travels as a custom property rather than as one class per colour, so the ramp's
		// length lives in exactly the two places named above. Its letters travel beside it: a tone
		// that is dark enough to need white initials says so with a `-ink` token of its own, and every
		// other tone falls through to the ramp's shared ink. The fallback lives here rather than in
		// the stylesheet because the token name is what varies.
		//
		picture = (
			<span
				aria-hidden="true"
				className={`${className} tb-avatar--tone`}
				style={
					{
						"--avatar-tone": avatarTone(id),
						"--avatar-ink": `var(--color-avatar-${String(toneOf(id))}-ink, var(--color-on-avatar))`,
					} as CSSProperties
				}
			>
				{letters}
			</span>
		)
	}

	if (presence === undefined) return picture
	return (
		<span className={`tb-face tb-face--${size}`}>
			{picture}
			<span aria-hidden="true" className={`tb-presence tb-presence--${presence}`} />
		</span>
	)
}
